import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format, parseISO, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Receipt, Eye, Ban, ExternalLink, Loader2, Landmark, Undo2, AlertTriangle, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FacturationTab } from './dossier/FacturationTab';
import { getAuthToken } from '../lib/authToken';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ETATS = [
  { value: 'tous', label: 'Tous' },
  { value: 'a_facturer', label: 'À facturer', cls: 'bg-amber-100 text-amber-800' },
  { value: 'facture', label: 'Facturé', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'non_facturable', label: 'Non facturable', cls: 'bg-slate-200 text-slate-600' },
  { value: 'sans_df', label: 'Sans dossier financeur', cls: 'bg-red-100 text-red-700' },
];
const ETAT = Object.fromEntries(ETATS.map(e => [e.value, e]));
const eur = (v) => (Number(v) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

export default function FacturationChainePage() {
  const navigate = useNavigate();
  const [periode, setPeriode] = useState(() => localStorage.getItem('facturation_chaine_periode') || format(new Date(), 'yyyy-MM'));
  const [data, setData] = useState({ rows: [], compteurs: {} });
  const [loading, setLoading] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [tri, setTri] = useState({ col: null, dir: 'asc' });
  const [etat, setEtat] = useState('tous');
  const [financeurs, setFinanceurs] = useState([]);
  const [facturer, setFacturer] = useState(null); // { row, dossier }
  const [nonFact, setNonFact] = useState(null); // row
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/facturation/chaine`, { params: { periode } });
      setData(res.data);
      localStorage.setItem('facturation_chaine_periode', periode);
    } catch { toast.error('Erreur de chargement'); } finally { setLoading(false); }
  }, [periode]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API_URL}/financeurs`).then(r => setFinanceurs(r.data || [])).catch(() => {}); }, []);

  const rows = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    const texte = (r) => [r.code, r.intitule, r.client, r.df?.financeur_nom, r.df?.code, ...(r.df?.bdc_mois || []), ...(r.factures || []).map(f => f.numero)].filter(Boolean).join(' ').toLowerCase();
    const liste = data.rows.filter(r => (etat === 'tous' || r.etat === etat) && (!q || texte(r).includes(q)));
    if (!tri.col) return liste;
    const val = (r) => ({ code: r.code || '', intitule: r.intitule || '', client: r.client || '', nb_jours: r.nb_jours || 0, df: r.df?.financeur_nom || '', etat: ETAT[r.etat]?.label || '' })[tri.col];
    return [...liste].sort((a, b) => { const x = val(a), y = val(b); const c = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), 'fr', { sensitivity: 'base' }); return tri.dir === 'asc' ? c : -c; });
  }, [data.rows, recherche, etat, tri]);
  const trier = (col) => setTri(t => t.col === col ? (t.dir === 'asc' ? { col, dir: 'desc' } : { col: null, dir: 'asc' }) : { col, dir: 'asc' });
  const Entete = ({ col, children }) => {
    const actif = tri.col === col;
    const Icone = !actif ? ArrowUpDown : tri.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <button type="button" onClick={() => trier(col)} className={`flex items-center gap-1 uppercase text-left hover:text-slate-700 ${actif ? 'text-indigo-600' : ''}`} title="Trier" data-testid={`chaine-tri-${col}`}>
        {children}<Icone className={`w-3 h-3 ${actif ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    );
  };
  const shift = (n) => setPeriode(format(n > 0 ? addMonths(parseISO(`${periode}-01`), 1) : subMonths(parseISO(`${periode}-01`), 1), 'yyyy-MM'));
  const moisLabel = format(parseISO(`${periode}-01`), 'MMMM yyyy', { locale: fr });

  const ouvrirFacturation = async (row) => {
    try {
      const res = await axios.get(`${API_URL}/dossiers/${row.dossier_id}`);
      setFacturer({ row, dossier: res.data });
    } catch { toast.error("Impossible de charger l'AF"); }
  };

  const voirPdf = async (fac) => {
    try {
      const res = await axios.get(`${API_URL}/documents/${fac.id}/pdf`, { responseType: 'blob', headers: { Authorization: `Bearer ${getAuthToken()}` } });
      window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch { toast.error('PDF indisponible'); }
  };

  const toggleNonFacturable = async (row, on) => {
    try {
      await axios.patch(`${API_URL}/dossiers-financeurs/${row.df.id}/non-facturable`, { periode, non_facturable: on, note });
      toast.success(on ? `${row.df.code} : ${moisLabel} non facturable` : `${row.df.code} : ${moisLabel} de nouveau à facturer`);
      setNonFact(null); setNote('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <DashboardLayout>
    <div className="space-y-5" data-testid="facturation-chaine-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-6 h-6 text-emerald-600" /> Facturation à la chaîne</h1>
          <p className="text-sm text-slate-500">Toutes les actions planifiées sur le mois, par dossier financeur — facturez sans ouvrir chaque AF</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="chaine-mois-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Input type="month" value={periode} onChange={e => e.target.value && setPeriode(e.target.value)} className="w-44" data-testid="chaine-periode" />
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="chaine-mois-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" onClick={() => navigate('/facturation')}>Retour</Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={recherche} onChange={e => setRecherche(e.target.value)} placeholder="Rechercher : AF, intitulé, client, financeur, BDC, n° facture…" className="pl-9" data-testid="chaine-recherche" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {ETATS.map(e => (
            <button key={e.value} type="button" onClick={() => setEtat(e.value)} className={`text-xs px-3 py-1.5 rounded-full border ${etat === e.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-testid={`chaine-etat-${e.value}`}>
              {e.label}{e.value !== 'tous' && <span className="ml-1 opacity-70">({data.compteurs?.[e.value] ?? 0})</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : rows.length === 0 ? (
        <Card className="border-dashed p-10 text-center text-slate-500">Aucune action planifiée en {moisLabel}{recherche || etat !== 'tous' ? ' pour ces filtres' : ''}.</Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-[110px_1fr_1fr_70px_1fr_170px_240px] gap-3 px-4 py-2 bg-slate-50 text-[10px] uppercase text-slate-400 font-medium">
            <Entete col="code">AF</Entete><Entete col="intitule">Intitulé</Entete><Entete col="client">Client</Entete><Entete col="nb_jours">Jours</Entete><Entete col="df">Dossier financeur</Entete><Entete col="etat">État</Entete><span className="text-right">Actions</span>
          </div>
          <div className="divide-y">
            {rows.map((r, i) => (
              <div key={`${r.dossier_id}-${r.df?.id || 'nodf'}`} className={`grid grid-cols-[110px_1fr_1fr_70px_1fr_170px_240px] gap-3 px-4 py-2.5 items-center text-sm ${r.etat === 'sans_df' ? 'bg-red-50/40' : ''}`} data-testid={`chaine-row-${i}`}>
                <button type="button" className="font-mono text-xs font-semibold text-indigo-700 text-left hover:underline" onClick={() => navigate(`/dossiers/${r.dossier_id}`)}>{r.code}<span className="block text-[9px] text-slate-400 uppercase">{r.type_dossier}</span></button>
                <span className="truncate text-slate-800" title={r.intitule}>{r.intitule}</span>
                <span className="truncate text-slate-600">{r.client || '—'}</span>
                <span className="text-xs text-slate-600">{r.nb_jours} j</span>
                <div className="min-w-0">
                  {r.df ? (
                    <>
                      <p className="text-xs font-medium text-slate-800 truncate">{r.df.financeur_nom}</p>
                      <p className="text-[10px] text-slate-400">{r.df.code} · {r.df.modalite_facturation || '—'} · {eur(r.df.tarif_unitaire)}{r.df.bdc_mois?.length ? ` · BDC ${r.df.bdc_mois.join(', ')}` : r.df.nb_bdc ? ` · ${r.df.nb_bdc} BDC` : ''}</p>
                    </>
                  ) : <span className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Aucun dossier financeur</span>}
                </div>
                <div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ETAT[r.etat]?.cls}`}>{ETAT[r.etat]?.label}</span>
                  {r.etat === 'facture' && (r.factures || []).map(f => (
                    <button key={f.id} type="button" onClick={() => voirPdf(f)} className="mt-1 block text-left text-[11px] hover:underline" title={`Voir ${f.numero}`} data-testid={`chaine-facture-${f.id}`}>
                      <span className="flex items-center gap-1 font-mono text-emerald-700 whitespace-nowrap"><Eye className="w-3 h-3" />{f.numero}</span>
                      <span className="block font-semibold text-slate-800 whitespace-nowrap" data-testid={`chaine-facture-ht-${f.id}`}>{eur(f.montant_ht)} HT</span>
                    </button>
                  ))}
                  {r.etat === 'non_facturable' && r.non_facturable?.note && <p className="mt-1 text-[10px] text-slate-500 italic truncate" title={r.non_facturable.note}>{r.non_facturable.note}</p>}
                </div>
                <div className="flex items-center justify-end gap-1">
                  {r.etat === 'a_facturer' && <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => ouvrirFacturation(r)} data-testid={`chaine-facturer-${i}`}><Receipt className="w-3.5 h-3.5 mr-1" /> À facturer</Button>}
                  {r.etat === 'a_facturer' && <Button size="sm" variant="outline" className="h-7 text-xs" title="Marquer non facturable pour ce mois" onClick={() => { setNonFact(r); setNote(''); }} data-testid={`chaine-non-facturable-${i}`}><Ban className="w-3.5 h-3.5" /></Button>}
                  {r.etat === 'non_facturable' && <Button size="sm" variant="outline" className="h-7 text-xs" title="Remettre à facturer" onClick={() => toggleNonFacturable(r, false)} data-testid={`chaine-refacturer-${i}`}><Undo2 className="w-3.5 h-3.5" /></Button>}
                  {r.etat === 'sans_df' && <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-700" onClick={() => navigate(`/dossiers/${r.dossier_id}`)}><Landmark className="w-3.5 h-3.5 mr-1" /> Créer le DF</Button>}
                  {r.df && <Button size="sm" variant="ghost" className="h-7 text-xs" title="Voir le dossier financeur" onClick={() => navigate(`/dossiers/${r.dossier_id}`)} data-testid={`chaine-voir-df-${i}`}><ExternalLink className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Facturation d'une AF sans la quitter : composant Facturation complet, choix des mois pré-coché */}
      <Dialog open={!!facturer} onOpenChange={(o) => { if (!o) { setFacturer(null); load(); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="chaine-facturation-dialog">
          <DialogHeader>
            <DialogTitle>{facturer?.row.code} — {facturer?.row.intitule}</DialogTitle>
            <DialogDescription>Facturation de {moisLabel} pour {facturer?.row.df?.financeur_nom}. Le mois est pré-coché ; vérifiez puis générez.</DialogDescription>
          </DialogHeader>
          {facturer && <FacturationTab dossierId={facturer.row.dossier_id} formData={facturer.dossier} financeurs={financeurs} autoFacture={{ dfId: facturer.row.df.id, periode }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!nonFact} onOpenChange={(o) => !o && setNonFact(null)}>
        <DialogContent className="max-w-md" data-testid="chaine-non-facturable-dialog">
          <DialogHeader>
            <DialogTitle>Non facturable — {moisLabel}</DialogTitle>
            <DialogDescription>{nonFact?.code} · {nonFact?.df?.financeur_nom}. Indiquez pourquoi ce dossier financeur n'est pas facturé sur ce mois.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Explication (ex. facturé globalement en fin d'action, prise en charge directe, mois déjà inclus…)" data-testid="chaine-non-facturable-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNonFact(null)}>Annuler</Button>
            <Button className="bg-slate-700 hover:bg-slate-800" disabled={!note.trim()} onClick={() => toggleNonFacturable(nonFact, true)} data-testid="chaine-non-facturable-confirm"><Ban className="w-4 h-4 mr-1" /> Marquer non facturable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
